import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { GrupoUsuario, PermissionCatalogResponse } from '../../services/grupoUsuarioService';

interface GrupoPermissoesDialogProps {
  open: boolean;
  onClose: () => void;
  grupo: GrupoUsuario | null;
  catalog: PermissionCatalogResponse | null;
  onSave: (permissoes: string[]) => Promise<void>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`permissoes-tabpanel-${index}`}
      aria-labelledby={`permissoes-tab-${index}`}
      {...other}
      style={{ flex: 1, overflowY: 'auto' }}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `permissoes-tab-${index}`,
    'aria-controls': `permissoes-tabpanel-${index}`,
  };
}

const GrupoPermissoesDialog: React.FC<GrupoPermissoesDialogProps> = ({
  open,
  onClose,
  grupo,
  catalog,
  onSave,
}) => {
  const [localPermissions, setLocalPermissions] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && grupo) {
      setLocalPermissions(new Set(grupo.permissoes || []));
      setActiveTab(0);
    }
  }, [open, grupo]);

  const handlePermissionToggle = (code: string) => {
    const newPermissions = new Set(localPermissions);
    if (newPermissions.has(code)) {
      newPermissions.delete(code);
    } else {
      newPermissions.add(code);
    }
    setLocalPermissions(newPermissions);
  };

  const handleToggleModule = (modulePermissions: any[]) => {
    const newPermissions = new Set(localPermissions);
    const allSelected = modulePermissions.every(p => newPermissions.has(p.code));

    modulePermissions.forEach(p => {
      if (allSelected) {
        newPermissions.delete(p.code);
      } else {
        newPermissions.add(p.code);
      }
    });

    setLocalPermissions(newPermissions);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(Array.from(localPermissions));
    } finally {
      setIsSaving(false);
    }
  };

  const modules = catalog ? Object.keys(catalog.modules) : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle>Gerenciar Permissões - {grupo?.nome}</DialogTitle>
      
      {!catalog ? (
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </DialogContent>
      ) : (
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          <Box sx={{ borderRight: 1, borderColor: 'divider', minWidth: 200, display: 'flex', flexDirection: 'column' }}>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Módulos de Permissões"
              sx={{ borderRight: 1, borderColor: 'divider', flex: 1 }}
            >
              {modules.map((modulo, index) => (
                <Tab 
                  key={modulo} 
                  label={modulo} 
                  {...a11yProps(index)} 
                  sx={{ textTransform: 'capitalize', alignItems: 'flex-start', textAlign: 'left' }} 
                />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {modules.map((modulo, index) => {
              const permissions = catalog.modules[modulo];
              const allSelected = permissions.length > 0 && permissions.every(p => localPermissions.has(p.code));
              const someSelected = permissions.some(p => localPermissions.has(p.code));
              
              // Group permissions by their sub-module if they follow the pattern `module.submodule.action`
              const groupedPermissions: Record<string, typeof permissions> = { 'Geral': [] };
              permissions.forEach(p => {
                const parts = p.code.split('.');
                if (parts.length >= 3 && parts[0] === modulo) {
                  const groupName = parts[1].replace(/_/g, ' ');
                  if (!groupedPermissions[groupName]) {
                    groupedPermissions[groupName] = [];
                  }
                  groupedPermissions[groupName].push(p);
                } else {
                  groupedPermissions['Geral'].push(p);
                }
              });

              // Remove 'Geral' if empty
              if (groupedPermissions['Geral'].length === 0) {
                delete groupedPermissions['Geral'];
              }

              return (
                <CustomTabPanel key={modulo} value={activeTab} index={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {modulo}
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allSelected}
                          onChange={() => handleToggleModule(permissions)}
                        />
                      }
                      label="Selecionar Todos"
                    />
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  
                  {Object.entries(groupedPermissions).map(([groupName, groupPerms]) => (
                    <Box key={groupName} sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 'bold', mb: 1.5, color: 'text.secondary' }}>
                        {groupName}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                        {groupPerms.map((perm) => (
                          <Box key={perm.code} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                              {perm.label}
                            </Typography>
                            <Box sx={{ flex: 1, borderBottom: '1px dashed', borderColor: 'divider', mx: 2, mt: 1 }} />
                            <Switch
                              checked={localPermissions.has(perm.code)}
                              onChange={() => handlePermissionToggle(perm.code)}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </CustomTabPanel>
              );
            })}
          </Box>
        </Box>
      )}

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving || !catalog}>
          {isSaving ? <CircularProgress size={24} /> : 'Salvar Permissões'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrupoPermissoesDialog;
